import { useEffect, useState } from 'react';
import { ChevronRight } from 'react-feather';
import { Spinner } from 'react-bootstrap';

import RunningProcessSlideCard from './RunningProcessSlideCard';
import { ProcessCodeToSlideType, RunningProcessSlideTypeConfigMap } from './data';
import { ApiProcessEvent, RunningProcessSlide, SlideGroup } from './types';

import EmblaSlider from '../../../components/EmblaSlider';
import CardComponent from '../../../components/base/CardComponent';
import PagesHeader from '../../../components/base/PagesHeader';
import HorizontalRule from '../../../components/base/HorizontalRule';

import useLocale from '../../../hooks/useLocale';
import { getRunningProcessData } from '../../../api/service/company/overview/service';
import { LocaleCode } from '../../../types/locale';
import styles from '../../../assets/scss/6-pages/superAdmin/overview.module.scss';
import classNames from 'classnames';

const RunningProcess = () => {
  const { localize, lang } = useLocale();
  const [selectedGroupIndex, setSelectedGroupIndex] = useState<SlideGroup>(SlideGroup.Upcoming);
  const [slideData, setSlideData] = useState<RunningProcessSlide[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Map slide data to RunningProcessSlideCard components
  const slides = slideData.map((data, idx) => {
    const selectedSlideStype = RunningProcessSlideTypeConfigMap[`${data.group}_${data.type}`];
    const { icon, cardBg } = selectedSlideStype || RunningProcessSlideTypeConfigMap['default'];

    // Compute tail class name based on group and type
    const tailClassName = selectedSlideStype ? `${data.group.toLowerCase()}-${data.type.toLowerCase()}` : 'default';

    const iconAlt = data.title;
    const iconBg = '#fff';

    return (
      <RunningProcessSlideCard
        key={idx}
        id={'running-process-slide_' + idx}
        icon={icon}
        date={data.date}
        title={data.title}
        period={data.period}
        notifyText={data.notifyText}
        cardBg={cardBg}
        iconAlt={iconAlt}
        iconBg={iconBg}
        tailClassName={tailClassName}
        tail={idx !== slideData.length - 1}
      />
    );
  });

  const handleSlideChange = (index: number) => {
    setSelectedGroupIndex(index === 0 ? SlideGroup.Past : SlideGroup.Upcoming);
  };

  const sectionTitle =
    selectedGroupIndex === SlideGroup.Upcoming
      ? localize('GLOBAL.upcoming_processes')
      : localize('GLOBAL.past_processes');
  const sectionNote = selectedGroupIndex === SlideGroup.Upcoming ? localize('GLOBAL.upcoming_processes_note') : null;

  // Fetch and map API slide data
  const fetchRunningProcessData = async () => {
    setLoading(true);
    try {
      const apiData: ApiProcessEvent[] = await getRunningProcessData();
      if (Array.isArray(apiData)) {
        const mapped: RunningProcessSlide[] = apiData
          .map((item) => {
            const type = ProcessCodeToSlideType[item.code];
            const group = item.group === 'history' ? SlideGroup.Past : SlideGroup.Upcoming;
            if (!type) return null;
            return updateSlideLocaleData({
              group,
              type,
              date: item.cutOffDate,
              titleLocales: {
                en: item.name ?? '',
                sv: item.name_sv ?? '',
              },
              title: item.name,
              period: item.period,
              notifyTextLocales: {
                en: item.description ?? '',
                sv: item.description_sv ?? '',
              },
              notifyText: item.description,
            });
          })
          .filter(Boolean) as RunningProcessSlide[];

        mapped.sort((a, b) => {
          if (a.group === SlideGroup.Past && b.group === SlideGroup.Upcoming) return -1;
          if (a.group === SlideGroup.Upcoming && b.group === SlideGroup.Past) return 1;
          return 0;
        });
        setSlideData(mapped.length ? mapped : []);
      } else {
        setSlideData([]);
      }
    } catch (e) {
      setSlideData([]);
    } finally {
      setLoading(false);
    }
  };

  const updateSlideLocaleData = (item: RunningProcessSlide): RunningProcessSlide => {
    return {
      ...item,
      title: lang === LocaleCode.EN ? item.titleLocales.en : item.titleLocales.sv || item.titleLocales.en || '',
      notifyText:
        lang === LocaleCode.EN
          ? item.notifyTextLocales.en
          : item.notifyTextLocales.sv || item.notifyTextLocales.en || '',
    };
  };

  useEffect(() => {
    fetchRunningProcessData();
  }, []);

  useEffect(() => {
    if (slideData.length) {
      setSlideData((prev) => {
        return prev.map((item) => updateSlideLocaleData(item));
      });
    }
  }, [lang]);

  return (
    <div className="mt-2 getting-started-box">
      <CardComponent className="get-started-bg mb-0">
        <div className="d-flex justify-content-between">
          <PagesHeader pageTitle={localize('GLOBAL.the_running_process')} headingClass="fs-h3 mb-0" translate={false} />
          {slideData.length > 0 && (
            <div className="theme-text d-flex align-items-center cursor-pointer">
              <ChevronRight />
              <span className="font-weight-600">Full process calendar</span>
            </div>
          )}
        </div>
        <HorizontalRule height="1px" color="#d3d3d3" />
        {loading && <Spinner />}
        {!loading && slideData.length > 0 && (
          <div style={{ maxWidth: 900 }}>
            <p className="theme-text mb-1">{sectionTitle}</p>
            <EmblaSlider
              slides={slides}
              slidesToShow={4}
              navClassName={classNames('flat_arrow', styles.slideArrowNav)}
              onSlideChange={handleSlideChange}
            />
            {sectionNote && (
              <p className="theme-text mt-1">
                <strong className="theme-text fw-bolder">Jan23:</strong> {sectionNote} Jan 2024
              </p>
            )}
          </div>
        )}
        {!loading && !slideData.length && <p className="theme-text mb-0">No running processes found</p>}
      </CardComponent>
    </div>
  );
};

export default RunningProcess;
